import Tool from "./Tool";
import NodeLayer from "./NodeLayer";
import Rect from "./Rect";
import Editor from "./Editor";
import Vector2 from "./Vector2";
import { v4 as uuidv4 } from "uuid";
import ConnectionLayer from "./ConnectionLayer";
import { isEmpty, max, min, uniqBy } from "lodash";
import LayerGroup from "./LayerGroup";
import Layer from "./Layer";

export default class DuplicateSectionTool extends Tool {
  public constructor(editor: Editor) {
    super(editor, "duplicate_section");
  }

  calculateTranslation = (groupLayers: Array<Layer>): number => {
    const nodeLayers: NodeLayer[] = groupLayers.filter(
      (layer) => layer instanceof NodeLayer
    ) as NodeLayer[];

    if (nodeLayers.length === 0) return 0;

    const xMinValues = nodeLayers.map((node) => node.boundingRect.position.x);
    const xMaxValues = nodeLayers.map(
      (node) => node.boundingRect.position.x + node.boundingRect.size.x
    );

    const minX = min(xMinValues);
    const maxX = max(xMaxValues);

    const totalWidth = maxX - minX;

    return totalWidth + totalWidth / 4; // Adding 50px space
  };

  public duplicateNode(
    selectedLayer: NodeLayer,
    includeOutcomingConnections: boolean = true
  ): NodeLayer {
    const { position, size } = selectedLayer.boundingRect;
    const {
      questionText,
      questionType,
      choices,
      answer,
      answers,
      enabledName,
      finalMessage,
      redirectUrl,
      yesChoiceStyle,
      noChoiceStyle,
      choicesStyles,
      finalQuestionStatus,
      narrativeTemplate,
      referralUrlStatus,
      colorCode,
      outgoingConnections,
    } = selectedLayer;

    const groupLayer = this.editor.rootLayerGroup.layers;

    const additionalTranslate = this.calculateTranslation(groupLayer);

    const newPosition = position.add(new Vector2(additionalTranslate, 40));
    var duplicatedLayer = new NodeLayer(
      this.editor,
      uuidv4(),
      new Rect(newPosition, size)
    );
    duplicatedLayer.setAnswer(answer);
    duplicatedLayer.setAnswers(answers);
    duplicatedLayer.setChoicesStyles(choicesStyles);
    duplicatedLayer.setEnabledName(enabledName);
    duplicatedLayer.setEnabledReferralUrl(referralUrlStatus);
    duplicatedLayer.setFinalMessage(finalMessage);
    duplicatedLayer.setFinalQuestion(finalQuestionStatus);
    duplicatedLayer.setNarrativeTemplate(narrativeTemplate);
    duplicatedLayer.setNoChoiceStyle(noChoiceStyle);
    duplicatedLayer.setYesChoiceStyle(yesChoiceStyle);
    duplicatedLayer.setQuestionText(questionText);
    duplicatedLayer.setQuestionType(questionType);
    duplicatedLayer.setRedirectUrl(redirectUrl);
    duplicatedLayer.setColorCode(colorCode);
    duplicatedLayer.setChoices([...choices]);

    includeOutcomingConnections &&
      outgoingConnections.forEach((connection) =>
        duplicatedLayer.addOutgoingConnection(connection)
      );

    return duplicatedLayer;
  }

  public duplicateConnection(
    selectedConnection: ConnectionLayer,
    startNodeLayer: NodeLayer
  ): ConnectionLayer {
    if (
      selectedConnection == null ||
      !(selectedConnection instanceof ConnectionLayer)
    ) {
      return;
    }

    const { condition } = selectedConnection;

    const duplicatedConnection = new ConnectionLayer(
      this.editor,
      uuidv4(),
      startNodeLayer
    );
    duplicatedConnection.setCondition(condition);

    return duplicatedConnection;
  }

  public duplicateConnectionWithEndNode(
    selectedConnection: ConnectionLayer,
    startNodeLayer: NodeLayer,
    endNodeLayer: NodeLayer
  ): ConnectionLayer {
    if (
      selectedConnection == null ||
      !(selectedConnection instanceof ConnectionLayer)
    ) {
      return;
    }

    const { condition } = selectedConnection;

    const duplicatedConnection = new ConnectionLayer(
      this.editor,
      uuidv4(),
      startNodeLayer
    );
    duplicatedConnection.setCondition(condition);
    duplicatedConnection.setEndNode(endNodeLayer);

    return duplicatedConnection;
  }

  public duplicateSection(): boolean {
    var selectedLayer = this.editor.selectedLayer;
    const groupLayer = this.editor.rootLayerGroup.layers;
    if (selectedLayer == null || !(selectedLayer instanceof NodeLayer)) {
      return;
    }

    const duplicatedSections = this.duplicateItemsUnderBox(
      selectedLayer,
      groupLayer
    );

    const removeDuplicates = (arr: any[]): any[] => {
      const uniqueItems: any[] = [];
      arr.forEach((item) => {
        if (uniqueItems.indexOf(item) === -1) {
          uniqueItems.push(item);
        }
      });
      return uniqueItems;
    };

    // Use the removeDuplicates function to remove duplicates
    const uniqueDuplicatedSections = removeDuplicates(duplicatedSections);

    uniqueDuplicatedSections.forEach((layer) =>
      this.editor.rootLayerGroup.addLayer(layer)
    );

    this.editor.setSelectedLayer(uniqueDuplicatedSections[0]);
  }

  public duplicateItemsUnderBox(
    selectedLayer: NodeLayer,
    data: Array<Layer>,
    nodeMap: Map<string, NodeLayer> = new Map(),
    connectionMap: Map<string, ConnectionLayer> = new Map(),
    ignoreDuplicates: boolean = false
  ): Array<Layer> {
    const duplicatedItems: Array<Layer> = [];

    if (selectedLayer == null || !(selectedLayer instanceof NodeLayer)) {
      return;
    }

    // Check if the node has already been duplicated
    if (nodeMap.has(selectedLayer.id) && !ignoreDuplicates) {
      return duplicatedItems;
    }

    let duplicatedLayer = nodeMap.get(selectedLayer.id);

    if (!ignoreDuplicates) {
      duplicatedLayer = this.duplicateNode(selectedLayer, false);
      nodeMap.set(selectedLayer.id, duplicatedLayer);
      duplicatedItems.push(duplicatedLayer);
    }

    // Duplicate and update outgoing connections
    selectedLayer.outgoingConnections.forEach((connection) => {
      let duplicatedEndLayer = nodeMap.get(connection.endNode.id);

      if (!duplicatedEndLayer) {
        duplicatedEndLayer = this.duplicateNode(connection.endNode, false);
        nodeMap.set(connection.endNode.id, duplicatedEndLayer);
        duplicatedItems.push(duplicatedEndLayer);

        // Recursively duplicate connected nodes and their connections
        const connectedNodeDuplications = this.duplicateItemsUnderBox(
          connection.endNode,
          data,
          nodeMap,
          connectionMap,
          true
        );
        duplicatedItems.push(...connectedNodeDuplications);
      }

      const duplicatedConnection = this.duplicateConnectionWithEndNode(
        connection,
        duplicatedLayer,
        duplicatedEndLayer
      );

      if (!connectionMap.has(connection.id)) {
        connectionMap.set(connection.id, duplicatedConnection);
        duplicatedItems.push(duplicatedConnection);
        duplicatedLayer.addOutgoingConnection(duplicatedConnection);
        duplicatedEndLayer.addIncomingConnection(duplicatedConnection);
      }
    });

    return duplicatedItems;
  }
}
